import { type Ref, useId } from "react";

import { useUserSettings } from "../../app/userSettings";
import { HipaaBadge } from "../../components/ui/Badge";
import { SourceTile } from "../../components/ui/SourceTile";
import { formatClockRange } from "../../lib/dates";
import { sourceNames } from "../../lib/sources";
import { DropNote, PickedBadge } from "./BlockCard";
import { type CardBoard, PICKED, boardHandlers, onCardContextMenu } from "./cardBoard";
import type { TicketCardView, TicketLine } from "./board";
import { peekFacts } from "./peekFacts";
import { PEEK_HINT, PeekDetails } from "./PeekDetails";
import { TicketStateRow } from "./TicketStateRow";
import type { DropZone } from "./useDragToMerge";
import { PEEK_OUTLINE, PEEK_ROOM, PRESS_DIP, PRESS_EASE, usePeek } from "./usePeek";

function entriesText(n: number): string {
  return n === 1 ? "1 time entry" : `${String(n)} time entries`;
}

/** A line's times: "8:12–8:17", or none known for time entered in Autotask with only hours. */
function rangeOf(line: TicketLine, timeZone: string): string {
  return line.endedAt === null ? "No times in Autotask" : formatClockRange(line.startedAt, line.endedAt, timeZone);
}

/** A block's sources in words; "Logged here" for one whose activities aren't on the day. */
function blockName(line: TicketLine): string {
  return line.lanes.length > 0 ? sourceNames(line.lanes) : "Logged here";
}

/** The duration column of a line: the same width, so the durations line up. */
const DURATION = "min-w-12 shrink-0 text-right font-semibold whitespace-nowrap";

/**
 * A line of a ticket's card: a block's sources, its times and its duration;
 * time entered straight in Autotask dashed and muted, as its own card was.
 */
function CardLine({ line, timeZone, onBrand }: { line: TicketLine; timeZone: string; onBrand: boolean }) {
  const soft = onBrand ? "text-white/85" : "text-muted";
  if (line.kind === "entry") {
    return (
      <span
        data-part="ticket-line"
        className={
          "-mx-2 flex items-center gap-2 rounded-row border border-dashed px-2 py-[3px] text-[12.5px] " +
          (onBrand ? "border-white/40 text-white/85" : "border-line text-muted")
        }
      >
        <span className="min-w-0 flex-1 truncate font-medium">Entered in Autotask</span>
        <span className="shrink-0 font-mono text-xs">{rangeOf(line, timeZone)}</span>
        <span className={DURATION}>{line.duration}</span>
      </span>
    );
  }
  return (
    <span data-part="ticket-line" className="flex items-center gap-2 text-[12.5px]">
      <span className="flex shrink-0 gap-[5px]">
        {line.lanes.map((lane) => (
          <SourceTile key={lane} lane={lane} onBrand={onBrand} decorative />
        ))}
      </span>
      <span className={`min-w-0 flex-1 truncate font-medium ${soft}`}>{blockName(line)}</span>
      <span className={`shrink-0 font-mono text-xs ${soft}`}>{rangeOf(line, timeZone)}</span>
      <span className={DURATION}>{line.duration}</span>
    </span>
  );
}

/**
 * A ticket's one card in Logged - Open or Closed (ADR 0189; the owner's
 * decision of 2026-10-21): its client, its ticket, the total of its time
 * entries that day, and a line for each (a block's sources, times and
 * duration; time entered in Autotask dashed), then its ticket's state once.
 *
 * It's one button, as a block's card is (a button can't hold another): it
 * opens the popup of the ticket's earliest block, whose "On this ticket
 * today" reaches the others. While any of its blocks' popups is open, it's
 * the selected card. It flashes the logged green when its ticket has just
 * closed, and a block dragged over it can be dropped on its ticket while
 * that's open (ADR 0187).
 *
 * Held still, it peeks as a block's card does (ADR 0193), outlined (ADR
 * 0194, decision 7): its blocks' first three activities in time order, and
 * its facts, the ticket's status and age among them. Time entered in
 * Autotask has no activities to list.
 *
 * It has a menu, its first block's, and is selected among several as a
 * block's card is (ADR 0194, decisions 3 and 5).
 */
export function TicketCard({
  card,
  timeZone,
  selected,
  onSelect,
  justClosed = false,
  now,
  ref,
  drop,
  board,
}: {
  card: TicketCardView;
  timeZone: string;
  /** One of its blocks' popups is open. */
  selected: boolean;
  /** Opens a block's popup (ADR 0184): its earliest block's. */
  onSelect: (id: string) => void;
  /** Its ticket closed since this page last saw it (v0.9.0's P2b). */
  justClosed?: boolean;
  /** The time now, for its ticket's age ("open 2 d"); the time it's drawn by default. */
  now?: Date;
  /** The card's button, to move focus to (#247): every one of its blocks' card. */
  ref?: Ref<HTMLButtonElement>;
  /** Dropping a block on its ticket (ADR 0187). */
  drop?: DropZone | undefined;
  /** Its menu, and selecting several (ADR 0194): the day view's board. */
  board?: CardBoard | undefined;
}) {
  const { peekHoldMs } = useUserSettings();
  const hintId = useId();
  const [first] = card.blocks;
  const peek = usePeek({
    onOpen: () => {
      if (first !== undefined) {
        onSelect(first.id);
      }
    },
    holdMs: peekHoldMs,
    ref,
  });
  // Selected: Axon blue. Peeking, it's outlined instead, on its own background.
  const lit = selected && !peek.peeking;
  const soft = lit ? "text-white/85" : "text-muted";
  const picked = board?.picked === true && !lit;
  const at = now ?? new Date();
  return (
    <li
      className="relative motion-safe:animate-block-in"
      data-just-closed={justClosed || undefined}
      data-part="ticket-card"
      onDragEnter={drop?.onDragEnter}
      onDragOver={drop?.onDragOver}
      onDragLeave={drop?.onDragLeave}
      onDrop={drop?.onDrop}
      onContextMenu={onCardContextMenu(board)}
    >
      <button
        type="button"
        aria-pressed={selected || picked}
        aria-describedby={peek.available ? hintId : undefined}
        data-peeking={peek.peeking || undefined}
        data-picked={picked || undefined}
        {...peek.button}
        {...boardHandlers(board, peek)}
        className={
          `flex w-full cursor-pointer flex-col gap-3 rounded-card border px-4 py-3.5 text-left ${PRESS_EASE} ` +
          (lit ? "bg-brand text-white shadow-[0_14px_30px_var(--brand-shadow)]" : "bg-card text-text") +
          // Pressed: the brand outline at once, and a slight dip (ADR 0193,
          // decision 4). Peeking: the outline, and a shadow (ADR 0194, decision 7).
          // Selected among several: the ring and a tint (decision 5).
          (peek.peeking
            ? ` ${PEEK_OUTLINE} ${PEEK_ROOM}`
            : picked
              ? ` ${PICKED}`
              : lit || peek.pressing
                ? " border-brand"
                : " border-line hover:border-brand") +
          (picked && peek.peeking ? " bg-[image:linear-gradient(var(--brand-tint),var(--brand-tint))]" : "") +
          (peek.pressing ? ` ${PRESS_DIP}` : "") +
          (justClosed ? " motion-safe:animate-logged-flash" : "")
        }
      >
        <span className="flex items-end justify-between gap-2.5">
          <span className="flex min-w-0 flex-col gap-[3px]">
            <span className="truncate text-base font-[650] tracking-[-0.015em]">{card.title}</span>
            {/* The ticket it went to, never its ID (Idea #288). */}
            <span className={`text-[12.5px] ${soft}`}>
              {card.ticket.number === null ? "Logged to one ticket" : `Ticket ${card.ticket.number}`}
            </span>
          </span>
          <span className="text-[30px] leading-none font-semibold tracking-[-0.04em]">{card.duration}</span>
        </span>
        {/* Its name says how many, as the lines show them. */}
        <span className="sr-only">{entriesText(card.lines.length)}</span>
        <span className="flex flex-col gap-1.5">
          {card.lines.map((line) => (
            <CardLine key={line.id} line={line} timeZone={timeZone} onBrand={lit} />
          ))}
        </span>
        <TicketStateRow ticket={card.ticket} now={at} timeZone={timeZone} onBrand={lit} />
        {card.isHipaa ? <HipaaBadge onBrand={lit} /> : null}
        {peek.peeking ? (
          <PeekDetails
            blocks={card.blocks}
            timeZone={timeZone}
            facts={peekFacts(card.blocks, { draftNotes: null, now: at, timeZone })}
          />
        ) : null}
      </button>
      {peek.available ? (
        <span id={hintId} hidden>
          {PEEK_HINT}
        </span>
      ) : null}
      {picked ? <PickedBadge /> : null}
      {drop?.hint ? <DropNote hint={drop.hint} /> : null}
    </li>
  );
}

/**
 * In a logged block's popup, when its ticket has other time entries that day
 * (ADR 0189): each, with its times and duration, and their total. This block
 * is marked; each other block is a button that shows its popup instead, as
 * Previous and Next do; time entered in Autotask is plain text, as it has no
 * popup. Close ticket, below, closes the ticket once, for all of them.
 */
export function OnThisTicket({
  card,
  blockId,
  timeZone,
  onSelect,
}: {
  card: TicketCardView;
  /** The popup's block. */
  blockId: string;
  timeZone: string;
  /** Shows another block in the popup. */
  onSelect?: ((blockId: string) => void) | undefined;
}) {
  const headingId = useId();
  const row = "flex w-full items-center gap-2.5 rounded-row px-3 py-2 text-[13px]";
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-1.5">
      <h3 id={headingId} className="text-[12.5px] font-[650] tracking-[0.08em] text-muted uppercase">
        On this ticket today
      </h3>
      <ul className="flex flex-col gap-1.5">
        {card.lines.map((line) => {
          const range = <span className="shrink-0 font-mono text-xs text-muted">{rangeOf(line, timeZone)}</span>;
          const duration = <span className={DURATION}>{line.duration}</span>;
          if (line.kind === "entry") {
            return (
              <li key={line.id} className={`${row} border border-dashed border-line text-muted`}>
                <span className="min-w-0 flex-1 truncate font-medium">Entered in Autotask</span>
                {range}
                {duration}
              </li>
            );
          }
          const tiles = (
            <span className="flex shrink-0 gap-[5px]">
              {line.lanes.map((lane) => (
                <SourceTile key={lane} lane={lane} decorative />
              ))}
            </span>
          );
          if (line.id === blockId) {
            return (
              <li key={line.id} aria-current="true" className={`${row} border border-brand bg-brand-tint`}>
                {tiles}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-semibold">{blockName(line)}</span>
                  <span className="text-[11px] font-bold tracking-[0.05em] text-brand-ink-on-tint uppercase">This block</span>
                </span>
                {range}
                {duration}
              </li>
            );
          }
          return (
            <li key={line.id}>
              <button
                type="button"
                onClick={() => {
                  onSelect?.(line.id);
                }}
                className={`${row} cursor-pointer border border-line bg-panel text-left hover:border-brand`}
              >
                {tiles}
                <span className="min-w-0 flex-1 truncate font-semibold">{blockName(line)}</span>
                {range}
                {duration}
              </button>
            </li>
          );
        })}
      </ul>
      <p className="flex items-center gap-2.5 px-3 text-[13px]">
        <span className="min-w-0 flex-1 text-muted">{`Total · ${entriesText(card.lines.length)}`}</span>
        <span className="shrink-0 text-right text-base font-semibold tracking-[-0.02em] whitespace-nowrap">{card.duration}</span>
      </p>
    </section>
  );
}
